import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import DatePicker from "../../../../../views/components/common/textFields/DatePicker";
import { formatDateWithoutTime } from "../../../../../views/components/common/functions/formatDate";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { useStores } from "../../../use-stores";
import Grid2 from "@mui/material/Grid2";
import { listingStatusTabsStyles } from "../../../../../styles/listingStatusTabsSx";

const CampaignShipmentGap: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { campaignStore, userStore } = useStores();
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const canViewCampaigns = userStore.hasPermission([
    EmployeeRoles.VIEW_CAMPAIGNS,
  ]);
  const [selectedDate, setSelectedDeliveryDate] = useState<Dayjs | null>(() => {
    const queryParams = new URLSearchParams(location.search);
    const dateFromQuery = queryParams.get("date_utc");
    if (dateFromQuery) {
      const parsedDate = dayjs(dateFromQuery).startOf("day");
      if (parsedDate.isValid()) return parsedDate;
    }
    return dayjs().startOf("day");
  });

  const campaignId = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const id = Number(queryParams.get("campaign_id"));
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [location.search]);

  const dateUtc = useMemo(() => {
    if (selectedDate == null || !selectedDate.isValid()) {
      return dayjs().startOf("day").format("YYYY-MM-DD");
    }
    return selectedDate.startOf("day").format("YYYY-MM-DD");
  }, [selectedDate]);
  const updateDateInQuery = (date: Dayjs) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set("date_utc", date.startOf("day").format("YYYY-MM-DD"));
    navigate(
      {
        pathname: location.pathname,
        search: `?${queryParams.toString()}`,
      },
      { replace: true },
    );
  };
  const updateSearchInQuery = (search: string) => {
    const queryParams = new URLSearchParams(location.search);
    const trimmedSearch = search.trim();
    if (trimmedSearch) {
      queryParams.set("search", trimmedSearch);
    } else {
      queryParams.delete("search");
    }
    navigate(
      {
        pathname: location.pathname,
        search: `?${queryParams.toString()}`,
      },
      { replace: true },
    );
  };
  const prefetchedTodayCountKeyRef = useRef<string>("");

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const searchFromQuery = (queryParams.get("search") || "").trim();
    if (!searchFromQuery) return;
    campaignStore.setEligibleEmployeesSearchInput(searchFromQuery);
    campaignStore.applyEligibleEmployeesFilters();
    campaignStore.setTodayShipmentsSearchInput(searchFromQuery);
    campaignStore.applyTodayShipmentsFilters();
  }, [campaignStore, location.search]);

  useEffect(() => {
    if (!canViewCampaigns) return;
    if (!campaignId) {
      campaignStore.clearCampaignDetailsState();
      return;
    }
    if (campaignStore.campaignDetailsTab === "eligible_employees") {
      campaignStore.getCampaignEligibleEmployees(campaignId, dateUtc);
    } else {
      campaignStore.getCampaignTodayShipments(campaignId, dateUtc);
    }
  }, [
    campaignId,
    canViewCampaigns,
    campaignStore,
    campaignStore.campaignDetailsTab,
    campaignStore.eligibleEmployeesAppliedSearch,
    campaignStore.todayShipmentsAppliedSearch,
    campaignStore.todayShipmentsPageNo,
    campaignStore.todayShipmentsLimit,
    dateUtc,
  ]);

  useEffect(() => {
    return () => {
      campaignStore.clearCampaignDetailsState();
    };
  }, [campaignStore]);

  useEffect(() => {
    if (!canViewCampaigns || !campaignId) return;
    if (campaignStore.campaignDetailsTab === "today_shipments") return;

    const prefetchKey = `${campaignId}-${dateUtc ?? "all"}`;
    if (prefetchedTodayCountKeyRef.current === prefetchKey) return;
    prefetchedTodayCountKeyRef.current = prefetchKey;

    // Prefetch today's shipments once so summary count is visible before tab switch.
    campaignStore.getCampaignTodayShipments(campaignId, dateUtc);
  }, [
    canViewCampaigns,
    campaignId,
    dateUtc,
    campaignStore,
    campaignStore.campaignDetailsTab,
  ]);

  // Common debounce pattern:
  // while typing -> wait 500ms -> update appliedSearch; fetch effect will run.
  useEffect(() => {
    if (!canViewCampaigns) return;
    if (!campaignId) return;
    if (campaignStore.campaignDetailsTab !== "eligible_employees") return;

    const nextSearch = (
      campaignStore.eligibleEmployeesSearchInput || ""
    ).trim();
    const appliedSearch = (
      campaignStore.eligibleEmployeesAppliedSearch || ""
    ).trim();
    if (nextSearch === appliedSearch) return;

    const timer = window.setTimeout(() => {
      campaignStore.applyEligibleEmployeesFilters();
      updateSearchInQuery(nextSearch);
    }, 500);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canViewCampaigns,
    campaignId,
    campaignStore,
    campaignStore.campaignDetailsTab,
    campaignStore.eligibleEmployeesSearchInput,
    campaignStore.eligibleEmployeesAppliedSearch,
  ]);

  useEffect(() => {
    if (!canViewCampaigns) return;
    if (!campaignId) return;
    if (campaignStore.campaignDetailsTab !== "today_shipments") return;

    const nextSearch = (campaignStore.todayShipmentsSearchInput || "").trim();
    const appliedSearch = (
      campaignStore.todayShipmentsAppliedSearch || ""
    ).trim();
    if (nextSearch === appliedSearch) return;

    const timer = window.setTimeout(() => {
      campaignStore.applyTodayShipmentsFilters();
      updateSearchInQuery(nextSearch);
    }, 500);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canViewCampaigns,
    campaignId,
    campaignStore,
    campaignStore.campaignDetailsTab,
    campaignStore.todayShipmentsSearchInput,
    campaignStore.todayShipmentsAppliedSearch,
  ]);

  const getCellValue = (value: unknown) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const getFormattedCellValue = (key: string, value: unknown) => {
    if (key === "created_date" || key === "updated_date") {
      return formatDateWithoutTime(value);
    }
    if (key === "start_date") {
      return formatDateWithoutTime(value);
    }
    return getCellValue(value);
  };

  const handleSearchSubmit = () => {
    if (!campaignId) return;
    if (campaignStore.campaignDetailsTab === "eligible_employees") {
      updateSearchInQuery(campaignStore.eligibleEmployeesSearchInput);
      campaignStore.applyEligibleEmployeesFilters();
    } else {
      updateSearchInQuery(campaignStore.todayShipmentsSearchInput);
      campaignStore.applyTodayShipmentsFilters();
    }
  };

  const eligibleColumns = useMemo(
    () => [
      { key: "employee_id", label: "Employee ID", minWidth: 140 },
      { key: "name", label: "Employee Name", minWidth: 220 },
      { key: "email", label: "Email", minWidth: 220 },
      { key: "start_date", label: "Start Date", minWidth: 180 },
      { key: "created_date", label: "Created Date", minWidth: 180 },
      { key: "updated_date", label: "Updated Date", minWidth: 180 },
      { key: "employee_mobile_number", label: "Mobile", minWidth: 160 },
      { key: "employee_class", label: "Class", minWidth: 140 },
      { key: "employee_status", label: "Status", minWidth: 140 },
      { key: "group_company", label: "Group Company", minWidth: 180 },
      { key: "tshirt_size", label: "Tshirt Size", minWidth: 140 },
      { key: "present_address", label: "Present Address", minWidth: 260 },
      { key: "pincode", label: "Pincode", minWidth: 120 },
    ],
    [],
  );

  const eligibleRows = useMemo(() => {
    return (campaignStore.eligibleEmployees || []).map((row) => ({
      employee_id: row.employee_id ?? row.id,
      name: [row.first_name, row.last_name].filter(Boolean).join(" ").trim(),
      email: row.email ?? row.work_email,
      start_date: row.start_date,
      created_date: row.created_date,
      updated_date: row.updated_date,
      employee_mobile_number: row.employee_mobile_number,
      employee_class: row.employee_class,
      employee_status: row.employee_status,
      group_company: row.group_company,
      tshirt_size: row.tshirt_size,
      present_address: row.present_address,
      pincode: row.pincode,
    }));
  }, [campaignStore.eligibleEmployees]);

  const todayShipmentColumns = useMemo(
    () => [
      { key: "id", label: "ID", minWidth: 100 },
      { key: "order_id", label: "Order Id", minWidth: 140 },
      { key: "inventory_id", label: "Inventory Id", minWidth: 140 },
      { key: "campaign_id", label: "Campaign Id", minWidth: 140 },
      { key: "customer_name", label: "Customer Name", minWidth: 200 },
      { key: "email", label: "Email", minWidth: 220 },
      { key: "address", label: "Address", minWidth: 260 },
      { key: "status_label", label: "Status", minWidth: 160 },
      { key: "created_date", label: "Created Date", minWidth: 180 },
      { key: "awb", label: "AWB", minWidth: 180 },
    ],
    [],
  );

  const todayShipmentRows = useMemo(() => {
    return (campaignStore.todayShipments || []).map((s) => ({
      id: (s as any)?.id,
      order_id: (s as any)?.order_id ?? (s as any)?.shopify_order_id,
      inventory_id: (s as any)?.inventory_id,
      campaign_id: (s as any)?.campaign_id,
      customer_name: (s as any)?.customer_name,
      email: (s as any)?.email,
      address: [
        (s as any)?.city,
        (s as any)?.state,
        (s as any)?.country,
        (s as any)?.pincode,
      ]
        .filter(Boolean)
        .join(", "),
      status_label: (s as any)?.status_label,
      created_date: (s as any)?.created_date,
      awb: (s as any)?.awb,
    }));
  }, [campaignStore.todayShipments]);

  if (!canViewCampaigns) {
    return <PermissionBlockLabel title="Campaign Details" />;
  }

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Box sx={{ p: 3 }}>
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <CustomTypography variant="heading2" fontWeight={700}>
                Campaign Details
              </CustomTypography>
            </Box>
            <AppButton
              variant="transparentPrimary"
              label="Back to Campaign Listing"
              onClick={() => navigate("/campaigns/listing")}
            />
          </Box>

          {!campaignId && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Campaign id is missing. Open this page from Campaign Listing.
            </Alert>
          )}

          {(campaignStore.eligibleEmployeesErrorMessage ||
            campaignStore.todayShipmentsErrorMessage) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {campaignStore.eligibleEmployeesErrorMessage ||
                campaignStore.todayShipmentsErrorMessage}
            </Alert>
          )}

          <Paper
            variant="outlined"
            sx={{
              mb: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsFiltersExpanded((v) => !v)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isFiltersExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <CustomTypography variant="body2" fontWeight={600}>
                Search & Filters
              </CustomTypography>
              <IconButton size="small">
                {isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
              <Box>
                <Box sx={{ p: 2, pb: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1.5,
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 300 } }}>
                      <FormTextField
                        id="campaign-details-search"
                        placeholder={
                          campaignStore.campaignDetailsTab ===
                          "eligible_employees"
                            ? "Search by Employee Name / Email"
                            : "Search by email"
                        }
                        type="search"
                        variant="outlined"
                        sx={{ mb: 0 }}
                        value={
                          campaignStore.campaignDetailsTab ===
                          "eligible_employees"
                            ? campaignStore.eligibleEmployeesSearchInput
                            : campaignStore.todayShipmentsSearchInput
                        }
                        onChange={(_, value) => {
                          if (
                            campaignStore.campaignDetailsTab ===
                            "eligible_employees"
                          ) {
                            campaignStore.setEligibleEmployeesSearchInput(
                              value || "",
                            );
                          } else {
                            campaignStore.setTodayShipmentsSearchInput(
                              value || "",
                            );
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSearchSubmit();
                          }
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                        justifyContent: { xs: "flex-end", sm: "flex-start" },
                      }}
                    >
                      <AppButton
                        variant="primary"
                        label="Apply"
                        onClick={handleSearchSubmit}
                        customStyles={{ minWidth: 120, height: 40 }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    borderTop: "1px solid",
                    borderColor: "divider",
                    mt: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      pt: 2.5,
                      pb: 2.5,
                      maxWidth: { xs: "100%", sm: 420 },
                    }}
                  >
                    <CustomTypography
                      variant="heading6"
                      style={{ fontWeight: 600, marginBottom: 8 }}
                    >
                      Select Date
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      style={{
                        marginBottom: 16,
                        lineHeight: "1.2",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Choose a date to filter.
                    </CustomTypography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1.5,
                        alignItems: { xs: "stretch", sm: "center" },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <DatePicker
                          label=""
                          value={selectedDate}
                          onChange={(date) => {
                            if (!date) {
                              const today = dayjs().startOf("day");
                              setSelectedDeliveryDate(today);
                              updateDateInQuery(today);
                              return;
                            }
                            const normalizedDate = dayjs(date).startOf("day");
                            const nextDate = normalizedDate.isValid()
                              ? normalizedDate
                              : dayjs().startOf("day");
                            setSelectedDeliveryDate(nextDate);
                            updateDateInQuery(nextDate);
                          }}
                          popperZIndex={3000}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: { xs: "flex-end", sm: "flex-start" },
                          flexShrink: 0,
                        }}
                      >
                        <AppButton
                          variant="primary"
                          label="Clear"
                          onClick={() => {
                            const today = dayjs().startOf("day");
                            setSelectedDeliveryDate(today);
                            updateDateInQuery(today);
                          }}
                          disabled={selectedDate?.isSame(
                            dayjs().startOf("day"),
                            "day",
                          )}
                          customStyles={{ minWidth: 120, height: 40 }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Paper>

          <Box
            sx={{
              mb: 2,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            <CustomTypography variant="body2" fontWeight={600}>
              Campaign ID: {campaignId ?? "-"}
            </CustomTypography>
            <CustomTypography variant="body2" fontWeight={600}>
              Customer ID: {campaignStore.campaignDetailsCustomerId ?? "-"}
            </CustomTypography>
            <CustomTypography variant="body2" fontWeight={600}>
              Eligible Employees: {campaignStore.eligibleEmployees.length}
            </CustomTypography>
            <CustomTypography variant="body2" fontWeight={600}>
              Shipments: {campaignStore.todayShipmentsTotalCount}
            </CustomTypography>
          </Box>

          <Tabs
            value={campaignStore.campaignDetailsTab}
            onChange={(_, v) => campaignStore.setCampaignDetailsTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={(t) => ({ ...listingStatusTabsStyles(t), mb: 2 })}
          >
            <Tab value="eligible_employees" label="ELIGIBLE EMPLOYEES" />
            <Tab value="today_shipments" label="SHIPMENTS" />
          </Tabs>

          {campaignStore.campaignDetailsTab === "eligible_employees" ? (
            campaignStore.isEligibleEmployeesLoading ? (
              <Box
                sx={{
                  minHeight: "35vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Paper
                sx={{
                  marginTop: "0.6rem",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <TableContainer sx={{ maxHeight: window.innerHeight - 350 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor:
                            theme.palette.customColors?.lightGreyColor ||
                            theme.palette.action.hover,
                          "& th": { py: 1, height: 48 },
                        }}
                      >
                        {eligibleColumns.map((column) => (
                          <AppTableHeaderCell
                            key={column.key}
                            title={column.label}
                            fontSize="12px"
                            sx={{ minWidth: column.minWidth }}
                          />
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eligibleRows.map((row, index) => (
                        <TableRow
                          key={`${String(row.employee_id || "row")}-${index}`}
                          hover
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: theme.palette.action.hover,
                            },
                            "& td": {
                              fontSize: "12px",
                              py: 1,
                              height: 48,
                              lineHeight: 1.2,
                            },
                          }}
                        >
                          {eligibleColumns.map((column) => (
                            <AppTableRowCell
                              key={column.key}
                              sx={{ textAlign: "center" }}
                            >
                              {getFormattedCellValue(
                                column.key,
                                row[column.key as keyof typeof row],
                              )}
                            </AppTableRowCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {eligibleRows.length === 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      p: 4,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      backgroundColor:
                        theme.palette.customColors?.grayBackGroundColor ||
                        theme.palette.action.hover,
                    }}
                  >
                    <CustomTypography
                      variant="heading6"
                      fontWeight={600}
                      color={theme.palette.customColors?.greyTextColor}
                    >
                      No eligible employees found.
                    </CustomTypography>
                  </Box>
                )}
              </Paper>
            )
          ) : campaignStore.isTodayShipmentsLoading ? (
            <Box
              sx={{
                minHeight: "35vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              sx={{
                marginTop: "0.6rem",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <TableContainer sx={{ maxHeight: window.innerHeight - 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor:
                          theme.palette.customColors?.lightGreyColor ||
                          theme.palette.action.hover,
                        "& th": { py: 1, height: 48 },
                      }}
                    >
                      {todayShipmentColumns.map((column) => (
                        <AppTableHeaderCell
                          key={column.key}
                          title={column.label}
                          fontSize="12px"
                          sx={{ minWidth: column.minWidth }}
                        />
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayShipmentRows.map((row, index) => (
                      <TableRow
                        key={`${String(row.id || "row")}-${index}`}
                        hover
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: theme.palette.action.hover,
                          },
                          "& td": {
                            fontSize: "12px",
                            py: 1,
                            height: 48,
                            lineHeight: 1.2,
                          },
                        }}
                      >
                        {todayShipmentColumns.map((column) => (
                          <AppTableRowCell
                            key={column.key}
                            sx={{ textAlign: "center" }}
                          >
                            {getFormattedCellValue(
                              column.key,
                              row[column.key as keyof typeof row],
                            )}
                          </AppTableRowCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {todayShipmentRows.length === 0 &&
                !campaignStore.isTodayShipmentsLoading && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      p: 4,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      backgroundColor:
                        theme.palette.customColors?.grayBackGroundColor ||
                        theme.palette.action.hover,
                    }}
                  >
                    <CustomTypography
                      variant="heading6"
                      fontWeight={600}
                      color={theme.palette.customColors?.greyTextColor}
                    >
                      No shipments found for today.
                    </CustomTypography>
                  </Box>
                )}

              <AppTablePagenation
                fontSize="12px"
                totalCount={campaignStore.todayShipmentsTotalCount}
                limit={campaignStore.todayShipmentsLimit}
                pageNo={campaignStore.todayShipmentsPageNo}
                onPageChange={(_, newPage) =>
                  campaignStore.setTodayShipmentsPageNo(newPage)
                }
                handleRowsChange={(newLimit) => {
                  campaignStore.setTodayShipmentsLimit(newLimit);
                }}
              />
            </Paper>
          )}
        </Paper>
      </Box>
    </Grid2>
  );
};

export default observer(CampaignShipmentGap);
